import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Store} from '@ngrx/store';
import * as fromApplicationSettings from '@app/views/admin/application-settings/store/application-settings.reducers';
import {Observable, of, throwError} from 'rxjs';
import {DelegatedPermission} from '@app/views/sso/delegated-permissions/delegated-permission.model';
import {environment} from '@env/environment';
import {catchError, map} from 'rxjs/operators';

@Injectable()
export class DelegatedPermissionsService {
    constructor(
        private http: HttpClient,
        private store: Store<fromApplicationSettings.State>
    ) {
    }

    getAll(applicationGuid: string = null): Observable<DelegatedPermission[]> {
        const params = applicationGuid ? {applicationGuid: applicationGuid} : null;
        return this.http.get<DelegatedPermission[]>(`${environment.etxBaseURI}/api/delegation`, {params: params})
            .pipe(
                map((data: Object[]) => this.mapResponseDataList(DelegatedPermission, data)),
                catchError(this.handleError)
            );
    }

    get(applicationGuid: string, personGuid: string, role: string): Observable<DelegatedPermission> {
        const params = {
            applicationGuid: applicationGuid,
            personGuid: personGuid,
            role: role
        };
        return this.http.get<DelegatedPermission>(`${environment.etxBaseURI}/api/delegation`, {params: params})
            .pipe(
                map((data: Object) => this.mapResponseDataItem(DelegatedPermission, data)),
                catchError(this.handleError)
            );
    }

    create(permission: DelegatedPermission): Observable<DelegatedPermission[]> {
        const options = {params: new HttpParams()
            .append('children', !!permission?.children)
        };

        return this.http.post(`${environment.etxBaseURI}/api/delegation`, permission, options)
            .pipe(
                map((savedPermissions: DelegatedPermission[] | any) => {
                    if (!savedPermissions) {
                        throw Error;
                    }
                    return savedPermissions;
                }),
                catchError(this.handleError)
            );
    }

    update(permission: DelegatedPermission): Observable<DelegatedPermission[]> {
        const options = {params: new HttpParams()
            .append('children', !!permission?.children)
        };

        return this.http.put(`${environment.etxBaseURI}/api/delegation`, permission, options)
            .pipe(
                map((savedPermissions: DelegatedPermission[] | any) => {
                    if (!savedPermissions) {
                        throw Error;
                    }
                    return savedPermissions;
                }),
                catchError(this.handleError)
            );
    }

    delete(permission: DelegatedPermission): Observable<boolean> {
        return this.http.delete(`${environment.etxBaseURI}/api/delegation`, {body: permission})
            .pipe(
                map(({result}: { result }) => {
                    if ((result.status !== 200) && (result.statusText !== 'OK')) {
                        throw Error;
                    }
                    return result === 'ok';
                }),
                catchError(error => {
                    if ((error.status !== 200) && (error.statusText !== 'OK')) {
                        throw Error;
                    }
                    return of(true);
                })
            );
    }

    private mapResponseDataItem<T>(model: new () => T, item: Object): T {
        return Object.assign(new model(), item);
    }

    private mapResponseDataList<T>(model: new () => T, list: Object[]): T[] {
        return list.map((item: T) => this.mapResponseDataItem(model, item));
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(`Backend returned code ${error.status}, body was: ${error.message}`);
        }
        return throwError(typeof error.error === 'string' ? error.error : 'Something bad happened, please try again later.');
    }
}
